// apps/provider/src/services/api/eligibility.js
// Eligibility check list and detail methods
//
// Methods: getEligibilityChecks, getEligibilityCheck

/**
 * @returns {Object} Eligibility domain methods (bound to `this` of the composed apiService)
 */
export function createEligibilityMethods() {
  return {
    /**
     * Get paginated eligibility checks with optional filters.
     *
     * Xano: GET /eligibility-checks
     *
     * @param {Object} [filters]
     * @param {number} [filters.page]
     * @param {number} [filters.limit]
     * @param {string} [filters.patient_id]
     * @param {string} [filters.start_date]
     * @param {string} [filters.end_date]
     * @returns {Promise<{ items: Object[], itemsTotal: number, curPage: number, nextPage: number, prevPage: number, pageSize: number }>}
     */
    async getEligibilityChecks(filters = {}) {
      const queryParams = new URLSearchParams();

      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.patient_id) queryParams.append('patient_id', filters.patient_id);
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);

      return this.request(`/eligibility-checks?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Get a single eligibility check by ID.
     *
     * Xano: GET /eligibility-checks/{id}
     *
     * @param {string|number} id
     * @returns {Promise<Object>}
     */
    async getEligibilityCheck(id) {
      return this.request(`/eligibility-checks/${id}`, {
        method: 'GET'
      });
    },

    /**
     * Run a ClaimMD eligibility check for one insurance entry.
     *
     * Xano: POST /eligibility/check
     *
     * A clearinghouse rejection (e.g. ClaimMD 58 Invalid/Missing DOB) resolves, not throws,
     * with `success: false, unverified: true, claimmd_errors: [{code, message}]`.
     *
     * @param {{ patient_id: string, insurance_member_id: string, insurance_is_primary: boolean, organization_tax_id: string, payer_id?: string }} body
     * @returns {Promise<import('@prism-ehr/shared').EligibilityCheckResponse>}
     */
    async checkEligibility(body) {
      return this.request('/eligibility/check', {
        method: 'POST',
        body: JSON.stringify(body)
      });
    },

    /**
     * Check eligibility for all insurance entries on a patient.
     *
     * Xano: POST /eligibility/check-all
     *
     * @param {{ patient_id: string, organization_tax_id: string }} params
     * @returns {Promise<import('@prism-ehr/shared').EligibilityCheckAllResponse>}
     */
    async checkAllEligibility({ patient_id, organization_tax_id }) {
      return this.request('/eligibility/check-all', {
        method: 'POST',
        body: JSON.stringify({ patient_id, organization_tax_id })
      });
    },

    /**
     * Get the latest eligibility check for a patient.
     *
     * @param {string} patientId
     * @returns {Promise<Object|null>}
     */
    async getLatestEligibilityCheck(patientId) {
      const result = await this.getEligibilityChecks({ patient_id: patientId, limit: 1 });
      return result?.items?.[0] || null;
    }
  };
}
